import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Minus, Trash2, Edit3, AlertTriangle, Search, ArrowDownUp, CheckCircle2, XCircle, Power, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY_FORM = {
  first_name: "", last_name: "", email: "", date_of_birth: "",
  phone: "",
  notes: "", lessons_remaining: 10, password: "",
  prima_prenotazione_effettuata: false,
};

export default function ClientsPage({ clients, onReload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name_asc");
  const [statusFilter, setStatusFilter] = useState("all"); // all | attivo | non_attivo
  const [form, setForm] = useState(EMPTY_FORM);

  const displayName = (c) => c.display_name || c.name ||
    `${c.first_name || ""} ${c.last_name || ""}`.trim();

  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    let base = clients;
    if (statusFilter !== "all") base = base.filter(c => (c.status || "attivo") === statusFilter);
    if (q) base = base.filter(c => displayName(c).toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q));
    const sorted = [...base];
    const remaining = (c) => (c.lessons_total || 0) - (c.lessons_used || 0);
    switch (sortBy) {
      case "lessons_asc":
        sorted.sort((a, b) => remaining(a) - remaining(b) || displayName(a).localeCompare(displayName(b)));
        break;
      case "lessons_desc":
        sorted.sort((a, b) => remaining(b) - remaining(a) || displayName(a).localeCompare(displayName(b)));
        break;
      case "name_asc":
      default:
        sorted.sort((a, b) => displayName(a).localeCompare(displayName(b)));
    }
    return sorted;
  }, [clients, q, sortBy, statusFilter]);

  const counts = useMemo(() => ({
    all: clients.length,
    attivo: clients.filter(c => (c.status || "attivo") === "attivo").length,
    non_attivo: clients.filter(c => c.status === "non_attivo").length,
  }), [clients]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      first_name: c.first_name || "", last_name: c.last_name || "",
      email: c.email, date_of_birth: c.date_of_birth || "",
      phone: c.phone || "",
      notes: c.notes || "",
      lessons_remaining: Math.max(0, (c.lessons_total || 0) - (c.lessons_used || 0)),
      password: "",
      prima_prenotazione_effettuata: !!c.prima_prenotazione_effettuata,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const newRemaining = Math.max(0, Number(form.lessons_remaining) || 0);
    try {
      if (editing) {
        const used = Number(editing.lessons_used || 0);
        await api.patch(`/clients/${editing.id}`, {
          first_name: form.first_name, last_name: form.last_name,
          date_of_birth: form.date_of_birth || null, notes: form.notes,
          lessons_total: newRemaining + used,
          prima_prenotazione_effettuata: !!form.prima_prenotazione_effettuata,
          phone: form.phone || undefined,
        });
        toast.success("Cliente aggiornato");
      } else {
        const { data } = await api.post("/clients", {
          email: form.email,
          first_name: form.first_name, last_name: form.last_name,
          date_of_birth: form.date_of_birth || null, notes: form.notes,
          phone: form.phone,
          lessons_total: newRemaining, lessons_used: 0,
          password: form.password || undefined,
        });
        toast.success(`Cliente creato. Password iniziale: ${data.initial_password}`);
      }
      setOpen(false); onReload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare ${displayName(c)}? Tutte le prenotazioni e schede saranno rimosse.`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success("Cliente eliminato"); onReload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleStatus = async (c) => {
    const current = c.status || "attivo";
    const next = current === "attivo" ? "non_attivo" : "attivo";
    let reason = null;
    if (next === "non_attivo") {
      reason = window.prompt(`Motivo per cui ${displayName(c)} passa a non attivo (opzionale):\nEs. infortunio, pausa personale, trasferimento…`, "");
      if (reason === null) return; // cancel
    } else {
      if (!window.confirm(`Rendere ${displayName(c)} attivo?`)) return;
    }
    try {
      await api.post(`/clients/${c.id}/status`, { status: next, reason });
      toast.success(next === "attivo" ? "Cliente attivo" : "Cliente non attivo");
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPassword = async (c) => {
    if (!window.confirm(`Generare una nuova password per ${displayName(c)}?`)) return;
    try {
      const { data } = await api.post(`/clients/${c.id}/reset-password`, {});
      window.prompt("Nuova password (copiala e comunicala al cliente):", data.new_password);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const daysSinceZero = (c) => {
    if (!c.zero_since) return null;
    const diff = (Date.now() - new Date(c.zero_since).getTime()) / (1000 * 60 * 60 * 24);
    return Math.floor(diff);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="label-caps">Rubrica clienti</div>
          <h2 className="font-heading font-black uppercase tracking-tighter text-3xl mt-1">
            {filtered.length}{filtered.length !== clients.length ? ` / ${clients.length}` : ""} clienti
          </h2>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="client-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome o email"
              className="pl-9 pr-3 py-2 bg-transparent border border-white/15 focus:border-[#A855F7] outline-none text-sm text-white w-64"
            />
          </div>
          <div className="relative flex items-center gap-2">
            <ArrowDownUp size={14} className="text-white/40" />
            <select
              data-testid="clients-sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none"
            >
              <option value="name_asc" className="bg-[#120B19]">Nome A-Z</option>
              <option value="lessons_asc" className="bg-[#120B19]">Lezioni residue: crescente</option>
              <option value="lessons_desc" className="bg-[#120B19]">Lezioni residue: decrescente</option>
            </select>
          </div>
          <button data-testid="add-client-btn" onClick={openCreate} className="btn-primary flex items-center gap-2">
            <Plus size={16} /> Nuovo cliente
          </button>
        </div>
      </div>

      {/* Status filter tabs */}
      <div className="inline-flex border border-white/10 mb-4">
        {[
          { key: "all", label: "Tutti", count: counts.all, testid: "filter-all" },
          { key: "attivo", label: "Attivi", count: counts.attivo, testid: "filter-attivo" },
          { key: "non_attivo", label: "Non attivi", count: counts.non_attivo, testid: "filter-non_attivo" },
        ].map(({ key, label, count, testid }) => (
          <button
            key={key}
            data-testid={testid}
            onClick={() => setStatusFilter(key)}
            className={`px-4 py-2 font-heading uppercase tracking-widest text-xs transition-colors border-l first:border-l-0 border-white/10 flex items-center gap-2
              ${statusFilter === key ? "bg-[#A855F7] text-white" : "text-white/60 hover:text-white"}`}
          >
            <span>{label}</span>
            <span className={`px-1.5 py-0.5 text-[10px] ${statusFilter === key ? "bg-white/20" : "bg-white/5"}`}>{count}</span>
          </button>
        ))}
      </div>

      <div className="card-surface table-scroll">
        <table className="w-full min-w-[900px]">
          <thead className="border-b border-white/10">
            <tr>
              {["Nome", "Email", "Data di nascita", "Lezioni residue", "Stato", "Note", ""].map(h => (
                <th key={h} className="label-caps text-left p-4">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const remaining = (c.lessons_total || 0) - (c.lessons_used || 0);
              const low = remaining <= 1;
              const status = c.status || "attivo";
              const isInactive = status === "non_attivo";
              const days = daysSinceZero(c);
              return (
                <tr key={c.id} data-testid={`client-row-${c.id}`} className={`border-b border-white/5 hover:bg-white/5 ${isInactive ? "opacity-70" : ""}`}>
                  <td className="p-4 font-medium">{displayName(c)}</td>
                  <td className="p-4 text-white/70 text-sm">{c.email}</td>
                  <td className="p-4 text-white/70 text-sm">{c.date_of_birth || "—"}</td>
                  <td className="p-4">
                    <span className={`inline-flex items-center gap-2 px-2 py-1 text-xs font-bold ${low ? "bg-[#FF3366]/15 text-[#FF3366] animate-pulse" : "bg-white/5 text-white"}`}>
                      {low && <AlertTriangle size={12} />}
                      {remaining}
                    </span>
                  </td>
                  <td className="p-4">
                    {isInactive ? (
                      <span className="inline-flex items-center gap-2 px-2 py-1 text-xs font-bold uppercase tracking-wider bg-[#FF3366]/15 text-[#FF3366]">
                        <XCircle size={12} /> Non attivo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2 px-2 py-1 text-xs font-bold uppercase tracking-wider bg-[#10B981]/10 text-[#10B981]">
                        <CheckCircle2 size={12} /> Attivo
                      </span>
                    )}
                    {c.status_manual && (
                      <div className="text-[10px] text-[#A855F7]/70 mt-1 uppercase tracking-widest">Manuale</div>
                    )}
                    {isInactive && c.inactive_reason && (
                      <div className="text-[10px] text-white/50 mt-1 italic max-w-[180px]">"{c.inactive_reason}"</div>
                    )}
                    {days !== null && status === "attivo" && !c.status_manual && (
                      <div className="text-[10px] text-white/40 mt-1">{days}gg a 0 lezioni</div>
                    )}
                  </td>
                  <td className="p-4 text-white/60 text-xs max-w-xs truncate">{c.notes}</td>
                  <td className="p-4 text-right whitespace-nowrap">
                    <button data-testid={`toggle-status-${c.id}`} onClick={() => toggleStatus(c)}
                      title={isInactive ? "Rendi attivo" : "Rendi non attivo"}
                      className={`p-2 ${isInactive ? "text-[#10B981]/80 hover:text-[#10B981]" : "text-white/70 hover:text-[#FF3366]"}`}>
                      <Power size={14} />
                    </button>
                    <button data-testid={`reset-pw-${c.id}`} onClick={() => resetPassword(c)}
                      title="Reset password" className="p-2 text-white/70 hover:text-[#A855F7]">
                      <KeyRound size={14} />
                    </button>
                    <button data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)} className="p-2 text-white/70 hover:text-white"><Edit3 size={14} /></button>
                    <button data-testid={`delete-client-${c.id}`} onClick={() => remove(c)} className="p-2 text-[#FF3366]/80 hover:text-[#FF3366]"><Trash2 size={14} /></button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-white/40">
                {clients.length === 0 ? "Nessun cliente ancora. Aggiungine uno." : "Nessun risultato con questi filtri."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#120B19] border-white/10 text-white rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">
            {editing ? "Modifica cliente" : "Nuovo cliente"}
          </DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Nome"><input required value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className="fld2" data-testid="cf-first-name" /></Field>
              <Field label="Cognome"><input required value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className="fld2" data-testid="cf-last-name" /></Field>
            </div>
            <Field label="Data di nascita"><input type="date" value={form.date_of_birth} onChange={e => setForm({ ...form, date_of_birth: e.target.value })} className="fld2" data-testid="cf-dob" /></Field>
            <Field label="Numero di telefono"><input required type="tel" pattern="[0-9+\-\s().]{6,25}" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Es. +39 334 3872824" className="fld2" data-testid="cf-phone" /></Field>
            {!editing && <Field label="Email"><input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="fld2" data-testid="cf-email" /></Field>}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Lezioni residue">
                <div className="flex items-center gap-2">
                  <button type="button" data-testid="cf-lessons-minus"
                    onClick={() => setForm({ ...form, lessons_remaining: Math.max(0, (Number(form.lessons_remaining) || 0) - 1) })}
                    className="border border-white/15 hover:border-[#A855F7] w-10 h-10 flex items-center justify-center shrink-0">
                    <Minus size={16} />
                  </button>
                  <input type="number" min="0" value={form.lessons_remaining}
                    onChange={e => setForm({ ...form, lessons_remaining: e.target.value })}
                    className="fld2 text-center" data-testid="cf-lessons-remaining" />
                  <button type="button" data-testid="cf-lessons-plus"
                    onClick={() => setForm({ ...form, lessons_remaining: (Number(form.lessons_remaining) || 0) + 1 })}
                    className="border border-white/15 hover:border-[#A855F7] w-10 h-10 flex items-center justify-center shrink-0">
                    <Plus size={16} />
                  </button>
                </div>
              </Field>
              <div />
            </div>
            <Field label="Note"><textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="fld2" data-testid="cf-notes" /></Field>
            {editing && (
              <label className="flex items-start gap-3 border border-white/10 p-3 cursor-pointer hover:border-[#A855F7] transition-colors">
                <input type="checkbox" checked={!!form.prima_prenotazione_effettuata}
                  onChange={e => setForm({ ...form, prima_prenotazione_effettuata: e.target.checked })}
                  data-testid="cf-first-booking-flag"
                  className="mt-1 w-4 h-4 accent-[#A855F7]" />
                <div className="min-w-0">
                  <div className="font-heading uppercase tracking-widest text-[11px] text-white">Prima prenotazione già effettuata</div>
                  <div className="text-[11px] text-white/50 mt-1">Se disattivato, il cliente può registrare una singola prenotazione anche con pacchetto a 0 residue. Il flag si attiva automaticamente alla prima prenotazione e va lasciato attivo. Reset consigliato solo in casi eccezionali.</div>
                </div>
              </label>
            )}
            {!editing && <Field label="Password iniziale (default: cliente123)">
              <input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="fld2" data-testid="cf-password" />
            </Field>}
            <button type="submit" data-testid="cf-submit" className="btn-primary mt-2">{editing ? "Salva" : "Crea cliente"}</button>
          </form>
          <style>{`.fld2{width:100%;background:transparent;border:1px solid rgba(255,255,255,0.15);padding:10px 12px;color:#fff;outline:none;font-family:'Manrope',sans-serif;font-size:0.95rem}.fld2:focus{border-color:#A855F7}`}</style>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="label-caps block mb-2">{label}</span>{children}</label>;
}
